import { useEffect, useState, ReactNode } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import { useAuth } from '../context/AuthContext';
import { auth } from '../config/firebase';
import './Recipe.css';
import '../pages/Landing.css';

/* ====================================================================
   Types — mirrors the backend recipes table / AI output schema
   ==================================================================== */

interface Ingredient {
  amount: string;
  unit: string;
  item: string;
  notes?: string | null;
}

interface Instruction {
  step: number;
  text: string;
  tip?: string | null;
}

interface Recipe {
  nanoid: string;
  name: string;
  meal: string;
  cuisine: string;
  servings?: number | null;
  difficulty: 'easy' | 'medium' | 'hard';
  nutrition_focus: string[];
  allergy_considerations: string[];
  tags: string[];
  prep_time_minutes: number;
  cook_time_minutes: number;
  total_time_minutes: number;
  ingredients: Ingredient[];
  instructions: Instruction[];
  wellness_benefits: string;
  description?: string | null;
  flavor_profile?: string[] | null;
  serving_suggestion?: string | null;
  base_recipe_refs?: string[] | null;
}

interface RecipeInteraction {
  status: 'suggested' | 'sent' | 'saved' | 'dismissed';
  made_it: boolean;
  feedback_rating: number | null;
  feedback_text: string | null;
  notes: string | null;
}

interface RecipeResponse extends Recipe {
  interaction?: RecipeInteraction;
}

/* ====================================================================
   Helpers
   ==================================================================== */

function formatTime(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatIngredient(ing: Ingredient): string {
  return [ing.amount, ing.unit, ing.item].filter(Boolean).join(' ');
}

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
};

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const token = await auth.currentUser?.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/* ====================================================================
   Interaction panel — only rendered for authenticated users
   ==================================================================== */

interface InteractionPanelProps {
  nanoid: string;
  interaction: RecipeInteraction;
  onChange: (updated: Partial<RecipeInteraction>) => void;
}

function InteractionPanel({ nanoid, interaction, onChange }: InteractionPanelProps) {
  const [patching, setPatching] = useState(false);

  const patch = async (payload: Partial<RecipeInteraction>) => {
    // Optimistic update
    onChange(payload);
    setPatching(true);
    try {
      const headers = await authHeaders();
      await fetch(`/api/recipes/${nanoid}/interaction`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(payload),
      });
    } catch {
      // silently fail — optimistic state stays
    } finally {
      setPatching(false);
    }
  };

  const isSaved = interaction.status === 'saved';
  const hasMadeIt = interaction.made_it;
  const rating = interaction.feedback_rating ?? 0;

  return (
    <div className={`recipe-interaction-bar${patching ? ' recipe-interaction-bar--saving' : ''}`}>

      {/* Save */}
      <button
        className={`recipe-action-btn${isSaved ? ' recipe-action-btn--active' : ''}`}
        onClick={() => !isSaved && patch({ status: 'saved' })}
        aria-label={isSaved ? 'Recipe saved' : 'Save recipe'}
        disabled={isSaved}
      >
        <span className="recipe-action-icon">{isSaved ? '🔖' : '🔖'}</span>
        <span className="recipe-action-label">{isSaved ? 'Saved' : 'Save'}</span>
      </button>

      <div className="recipe-interaction-divider" />

      {/* Made it */}
      <button
        className={`recipe-action-btn${hasMadeIt ? ' recipe-action-btn--active' : ''}`}
        onClick={() => !hasMadeIt && patch({ made_it: true })}
        aria-label={hasMadeIt ? "You've made this" : "Mark as made"}
        disabled={hasMadeIt}
      >
        <span className="recipe-action-icon">{hasMadeIt ? '✅' : '🍳'}</span>
        <span className="recipe-action-label">{hasMadeIt ? 'Made it!' : 'Made it?'}</span>
      </button>

      <div className="recipe-interaction-divider" />

      {/* Star rating */}
      <div className="recipe-stars" aria-label="Rate this recipe">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            className={`recipe-star${star <= rating ? ' recipe-star--filled' : ''}`}
            onClick={() => patch({ feedback_rating: star })}
            aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
          >
            ★
          </button>
        ))}
      </div>

    </div>
  );
}

/* ====================================================================
   Shared shell
   ==================================================================== */

function RecipeShell({ children }: { children: ReactNode }) {
  return (
    <div className="recipe-page">
      <PublicHeader />

      {children}

      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* ====================================================================
   Recipe detail page
   ==================================================================== */

export function RecipePage() {
  const { recipe_id } = useParams<{ recipe_id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [interaction, setInteraction] = useState<RecipeInteraction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [recipe_id]);

  useEffect(() => {
    if (!recipe_id) {
      setError('not_found');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`/api/recipes/${recipe_id}`, { headers });

        if (cancelled) return;

        if (res.status === 404) { setError('not_found'); setLoading(false); return; }
        if (!res.ok) { setError('error'); setLoading(false); return; }

        const json = await res.json();
        const data: RecipeResponse = json.data?.recipe ?? json;
        setRecipe(data);
        const inter = json.data?.user_interaction ?? data.interaction;
        if (inter) setInteraction(inter);
        setLoading(false);
      } catch {
        if (!cancelled) { setError('error'); setLoading(false); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [recipe_id]);

  const handleInteractionChange = (updated: Partial<RecipeInteraction>) => {
    setInteraction((prev) =>
      prev ? { ...prev, ...updated } : { status: 'sent', made_it: false, feedback_rating: null, feedback_text: null, notes: null, ...updated }
    );
  };

  if (loading) {
    return (
      <RecipeShell>
        <div className="recipe-loading">
          <div className="recipe-loading-spinner" />
          <p>Loading recipe…</p>
        </div>
      </RecipeShell>
    );
  }

  if (error || !recipe) {
    return (
      <RecipeShell>
        <div className="recipe-not-found">
          <div className="recipe-not-found-icon">🍽️</div>
          <h2>{error === 'not_found' ? 'Recipe not found' : 'Something went wrong'}</h2>
          <p>
            {error === 'not_found'
              ? "This recipe doesn't exist or may have been removed."
              : "We couldn't load this recipe. Please try again."}
          </p>
          <button className="btn hero-cta" onClick={() => navigate('/')}>
            Go Home
          </button>
        </div>
      </RecipeShell>
    );
  }

  return (
    <RecipeShell>
      {/* ---- Hero header ---- */}
      <header className="recipe-hero">
        <div className="landing-container recipe-hero-inner">

          <div className="recipe-meta-row">
            <span className="recipe-meta-pill recipe-meta-meal">{capitalize(recipe.meal)}</span>
            {recipe.cuisine && (
              <span className="recipe-meta-pill recipe-meta-cuisine">{recipe.cuisine}</span>
            )}
            <span className={`recipe-meta-pill recipe-meta-difficulty recipe-meta-difficulty--${recipe.difficulty}`}>
              {DIFFICULTY_LABEL[recipe.difficulty] ?? capitalize(recipe.difficulty)}
            </span>
            {recipe.servings && (
              <span className="recipe-meta-pill recipe-meta-servings">
                {recipe.servings} serving{recipe.servings !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          <h1 className="recipe-title">{recipe.name}</h1>

          {recipe.description && (
            <p className="recipe-description">{recipe.description}</p>
          )}

          {recipe.flavor_profile && recipe.flavor_profile.length > 0 && (
            <div className="recipe-flavor-row">
              {recipe.flavor_profile.map((f) => (
                <span key={f} className="recipe-flavor-chip">{f}</span>
              ))}
            </div>
          )}

          {/* Time cards */}
          <div className="recipe-time-row">
            <div className="recipe-time-card">
              <span className="recipe-time-label">Prep</span>
              <span className="recipe-time-value">{formatTime(recipe.prep_time_minutes)}</span>
            </div>
            <div className="recipe-time-divider" />
            <div className="recipe-time-card">
              <span className="recipe-time-label">Cook</span>
              <span className="recipe-time-value">{formatTime(recipe.cook_time_minutes)}</span>
            </div>
            <div className="recipe-time-divider" />
            <div className="recipe-time-card recipe-time-card--total">
              <span className="recipe-time-label">Total</span>
              <span className="recipe-time-value">{formatTime(recipe.total_time_minutes)}</span>
            </div>
          </div>

        </div>
      </header>

      {/* ---- Interaction panel (authenticated users only) ---- */}
      {isAuthenticated && (
        <div className="landing-container">
          <InteractionPanel
            nanoid={recipe.nanoid}
            interaction={interaction ?? { status: 'sent', made_it: false, feedback_rating: null, feedback_text: null, notes: null }}
            onChange={handleInteractionChange}
          />
        </div>
      )}

      <div className="landing-container recipe-body">

        {/* ---- Tags ---- */}
        <section className="recipe-tags-section">
          {recipe.nutrition_focus.length > 0 && (
            <div className="recipe-tags-group">
              <span className="recipe-tags-label">Nutrition</span>
              <div className="recipe-tags">
                {recipe.nutrition_focus.map((tag) => (
                  <span key={tag} className="recipe-tag recipe-tag--nutrition">{tag}</span>
                ))}
              </div>
            </div>
          )}
          {recipe.allergy_considerations.length > 0 && (
            <div className="recipe-tags-group">
              <span className="recipe-tags-label">Allergens</span>
              <div className="recipe-tags">
                {recipe.allergy_considerations.map((tag) => (
                  <span key={tag} className="recipe-tag recipe-tag--allergy">{tag}</span>
                ))}
              </div>
            </div>
          )}
          {recipe.tags.length > 0 && (
            <div className="recipe-tags-group">
              <span className="recipe-tags-label">Tags</span>
              <div className="recipe-tags">
                {recipe.tags.map((tag) => (
                  <span key={tag} className="recipe-tag recipe-tag--general">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ---- Two-column layout on wider screens ---- */}
        <div className="recipe-columns">

          {/* Ingredients */}
          <section className="recipe-card recipe-ingredients-card">
            <h2 className="recipe-card-title">
              <span className="recipe-card-icon">🧺</span> Ingredients
            </h2>
            <ul className="recipe-ingredient-list">
              {recipe.ingredients.map((ing, i) => (
                <li key={i} className="recipe-ingredient-item">
                  <span className="recipe-ingredient-check" aria-hidden="true" />
                  <span>
                    {formatIngredient(ing)}
                    {ing.notes && (
                      <span className="recipe-ingredient-note">, {ing.notes}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Instructions */}
          <section className="recipe-card recipe-instructions-card">
            <h2 className="recipe-card-title">
              <span className="recipe-card-icon">👩‍🍳</span> Instructions
            </h2>
            <ol className="recipe-instruction-list">
              {recipe.instructions.map((inst) => (
                <li key={inst.step} className="recipe-instruction-item">
                  <span className="recipe-step-num">{inst.step}</span>
                  <span className="recipe-step-body">
                    <span className="recipe-step-text">{inst.text}</span>
                    {inst.tip && (
                      <span className="recipe-step-tip">💡 {inst.tip}</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </section>

        </div>

        {/* ---- Wellness benefits ---- */}
        {recipe.wellness_benefits && (
          <section className="recipe-wellness-card">
            <div className="recipe-wellness-icon" aria-hidden="true">🌿</div>
            <div>
              <h2 className="recipe-wellness-title">Wellness Benefits</h2>
              <p className="recipe-wellness-text">{recipe.wellness_benefits}</p>
            </div>
          </section>
        )}

        {/* ---- Serving suggestion ---- */}
        {recipe.serving_suggestion && (
          <section className="recipe-serving-card">
            <h2 className="recipe-serving-title">Serving Suggestion</h2>
            <p className="recipe-serving-text">{recipe.serving_suggestion}</p>
          </section>
        )}

        {/* ---- CTA — only shown to unauthenticated visitors ---- */}
        {!isAuthenticated && (
          <div className="recipe-cta">
            <h3>Want personalised recipes like this?</h3>
            <p>mosaime builds meal plans around your goals, preferences, and lifestyle — delivered straight to your phone.</p>
            <Link to="/signup" className="btn hero-cta">Get Started Free</Link>
          </div>
        )}

      </div>
    </RecipeShell>
  );
}

export default RecipePage;
